import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import {
  useCopyToClipboard,
  useIsArtefactEditable,
} from '@modules/common/hooks';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useDuplicateDashboardAnalysis } from '@modules/dashboard/hooks/useDuplicateDashboardAnalysis';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { LinkRounded, RemoveCircleRounded } from '@mui/icons-material';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { AnalysisUseCase, AnalysisViewFragment } from 'generated/graphql';
import { useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { DashboardFeedExportFeedbackAction } from '../DashboardFeedExportFeedbackAction';
import { RemoveAnalysisDialogContent } from '../RemoveAnalysisDialogContent';
import { DashboardAnalysisHeaderFiltersInfoIcon } from './DashboardAnalysisHeaderFiltersInfoIcon';
import { DashboardQuantifyExportFeedbackAction } from './DashboardQuantifyExportFeedbackAction';
import { DashboardSwitchViewMenuItem } from './DashboardSwitchViewMenuItem';

interface DashboardAnalysisHeaderActionsProps {
  rowID: string;
  analysis: AnalysisViewFragment;
  analysisLink: string;
}

export const DashboardAnalysisHeaderActions = ({
  rowID,
  analysis,
  analysisLink,
}: DashboardAnalysisHeaderActionsProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchor);

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const { openModal } = useMuiModalContext();

  const editToken = dashboard?.editToken;

  const copyTextToClipboard = useCopyToClipboard();

  const { duplicateAnalysis } = useDuplicateDashboardAnalysis({ analysis });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCopyLink = () => {
    copyTextToClipboard(
      `${window.location.origin}/${tenantSlug}${analysisLink}`,
    );
    trackDashboardEvent({
      eventName: 'Analysis Link Copied from Dashboard',
      dashboardLink: window.location.href,
      analysisLink,
    });
    handleMenuClose();
  };

  const handleRemove = () => {
    if (!dashboard) {
      return;
    }
    openModal({
      contentComponent: () => (
        <RemoveAnalysisDialogContent
          dashboard={{
            ...dashboard,
            editToken: editToken ?? '',
          }}
          analysis={analysis}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
    handleMenuClose();
  };

  const isEditable = useIsArtefactEditable({
    editToken: dashboard.editToken,
    status: dashboard.status,
  });

  const handleDuplicate = () => {
    duplicateAnalysis({
      rowID,
    });
    handleMenuClose();
  };

  return (
    <Stack direction="row" alignItems="center">
      <DashboardAnalysisHeaderFiltersInfoIcon analysis={analysis} />
      <IconButton
        onClick={handleMenuClick}
        size="small"
        aria-label="dashboard-analysis-actions-menu"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <LinkRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        {analysis.useCase === AnalysisUseCase.Feed && (
          <DashboardFeedExportFeedbackAction />
        )}
        {analysis.useCase !== AnalysisUseCase.Feed && (
          <DashboardSwitchViewMenuItem
            analysis={analysis}
            closeMenu={handleMenuClose}
          />
        )}
        {isEditable && (
          <MenuItem onClick={handleDuplicate}>
            <ListItemIcon>
              <ControlPointDuplicateRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
        )}
        {isEditable && (
          <MenuItem onClick={handleRemove}>
            <ListItemIcon>
              <RemoveCircleRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from Dashboard</ListItemText>
          </MenuItem>
        )}
        {analysis.useCase !== AnalysisUseCase.Feed && (
          <DashboardQuantifyExportFeedbackAction
            analysis={analysis}
            analysisLink={analysisLink}
          />
        )}
      </Menu>
    </Stack>
  );
};
